/**
 * What a PRICING item's payload means, for display. Mirrors
 * `jcseo/boardroom/pricing.py` — keep the label rules in step.
 */
export type PricingPeriod = 'month' | 'year' | 'one_time';

export const PERIOD_OPTIONS: { key: PricingPeriod; label: string }[] = [
  { key: 'month', label: 'per month' },
  { key: 'year', label: 'per year' },
  { key: 'one_time', label: 'one-time' },
];

const SYMBOLS: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$' };

function money(amount: number, currency: string | null | undefined): string {
  const sym = SYMBOLS[currency ?? ''] ?? '';
  const num = Number.isInteger(amount) ? amount.toLocaleString() : amount.toFixed(2);
  return sym ? `${sym}${num}` : `${num} ${currency ?? ''}`.trim();
}

/** "$49/month per seat", "free", "$990 one-time", "not published". */
export function priceLabel(payload: Record<string, unknown> | null | undefined): string {
  const p = payload ?? {};
  const raw = p.amount;
  if (raw == null || raw === '') {
    const notes = typeof p.notes === 'string' ? p.notes.trim() : '';
    return notes ? notes.slice(0, 60) : 'not published';
  }
  const amount = Number(raw);
  if (!Number.isFinite(amount)) return 'not published';
  if (amount === 0) return 'free';
  const currency = typeof p.currency === 'string' ? p.currency : null;
  const period = typeof p.period === 'string' ? p.period : null;
  const unit = typeof p.unit === 'string' ? p.unit.trim() : '';
  const unitPart = unit && unit !== 'flat' ? ` ${unit}` : '';
  const m = money(amount, currency);
  if (period === 'one_time') return `${m} one-time${unitPart}`;
  if (period === 'month' || period === 'year') return `${m}/${period}${unitPart}`;
  return `${m}${unitPart}`;
}

/** A month's worth, for sorting plans; null for one-time and unpublished. */
export function monthlyEquivalent(payload: Record<string, unknown> | null | undefined): number | null {
  const p = payload ?? {};
  const amount = Number(p.amount);
  if (p.amount == null || !Number.isFinite(amount)) return null;
  if (p.period === 'month') return amount;
  if (p.period === 'year') return Math.round((amount / 12) * 100) / 100;
  return null;
}
